import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

export const createStaffReportApi = (payload) =>
  axios.post(`${API_BASE_URL}/api/staff-reporting`, payload);

export const updateStaffReportApi = (id, payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/${id}`, payload);

// PUT /api/staff-reporting/:id/verify-work — admin sign-off on one completed
// main / sub work. payload: { mainId, subId?, verified, remarks }
export const verifyStaffReportWorkApi = (id, payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/${id}/verify-work`, payload);

// PUT /api/staff-reporting/:id/hold-work — employee pauses one main/sub work.
// payload: { mainId, subId?, reason }
export const holdStaffReportWorkApi = (id, payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/${id}/hold-work`, payload);

// PUT /api/staff-reporting/:id/resume-work — employee resumes a held
// main/sub work with a brand-new completion date.
// payload: { mainId, subId?, completionDate }
export const resumeStaffReportWorkApi = (id, payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/${id}/resume-work`, payload);

// DELETE /api/staff-reporting/:id/work — admin removes one wrongly-created main
// work (with all its sub works) or a single sub work. body: { mainId, subId? }
export const deleteStaffReportWorkApi = (id, payload) =>
  axios.delete(`${API_BASE_URL}/api/staff-reporting/${id}/work`, {
    data: payload,
  });

// PUT /api/staff-reporting/:id/work-update — correct or delete one wrongly
// added daily update on a Field Execution Journal work.
// body: { mainId, subId?, original: { date, text, peopleCount }, text,
// peopleCount, remove? }
export const editStaffReportWorkUpdateApi = (id, payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/${id}/work-update`, payload);

// GET /api/staff-reporting/master-customers?formType=&employeeId= — the
// persistent customer roster for a Tele Calling / Real Estate Sales employee.
export const getMasterCustomersApi = (formType, employeeId) =>
  axios.get(`${API_BASE_URL}/api/staff-reporting/master-customers`, {
    params: { formType, ...(employeeId ? { employeeId } : {}) },
  });

// PUT /api/staff-reporting/master-customers — body { formType, employeeId?,
// customers }. Identity fields are frozen server-side once a customer has any
// logged remark.
export const updateMasterCustomersApi = (payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/master-customers`, payload);

// PUT /api/staff-reporting/master-customers/remark — append one dated remark to
// a single customer. body { formType, employeeId?, customerId, remarks, date? }
export const addMasterCustomerRemarkApi = (payload) =>
  axios.put(
    `${API_BASE_URL}/api/staff-reporting/master-customers/remark`,
    payload,
  );

// PUT /api/staff-reporting/master-customers/bulk — append many customers.
// body { formType, employeeId?, rows: [{ name, phone }] }
export const bulkAddMasterCustomersApi = (payload) =>
  axios.put(
    `${API_BASE_URL}/api/staff-reporting/master-customers/bulk`,
    payload,
  );

// PUT /api/staff-reporting/master-customers/share — hand off customers to a
// named employee of the other roster type (Tele Calling <-> Real Estate
// Sales). body { formType, employeeId?, customerIds: [...], toEmployeeId }
export const shareMasterCustomersApi = (payload) =>
  axios.put(
    `${API_BASE_URL}/api/staff-reporting/master-customers/share`,
    payload,
  );

// Bulk import of Field Execution Journal (formType 1) sheets from a parsed
// spreadsheet — payload: { employeeId, units: [...] }.
export const bulkImportFieldReportsApi = (payload) =>
  axios.post(`${API_BASE_URL}/api/staff-reporting/bulk-import`, payload);

export const getTechnicalEmployeesApi = () =>
  axios.get(`${API_BASE_URL}/api/employees/technical`);

// Every Field Execution Journal employee (PM, Civil Engineer, Supervisor,
// Foreman) — the transfer-work picker's options.
export const getFieldEmployeesApi = () =>
  axios.get(`${API_BASE_URL}/api/employees/technical`, {
    params: { scope: "field" },
  });

// POST /api/staff-reporting/:id/transfer — super-admin hands a whole Field
// Execution Journal sheet to another site employee. body: { employeeId, remarks? }
export const transferStaffReportApi = (id, payload) =>
  axios.post(`${API_BASE_URL}/api/staff-reporting/${id}/transfer`, payload);

// POST /api/staff-reporting/backdate-grants — super-admin lets one employee add
// reports for one past day. Body { employeeId, date: "YYYY-MM-DD", note?, hours? }
export const createBackdateGrantApi = (payload) =>
  axios.post(`${API_BASE_URL}/api/staff-reporting/backdate-grants`, payload);

// DELETE /api/staff-reporting/backdate-grants/:id — take a grant back.
export const revokeBackdateGrantApi = (id) =>
  axios.delete(`${API_BASE_URL}/api/staff-reporting/backdate-grants/${id}`);

// ─── Real Estate Sales — CP / Customer visitor list ──────────────────────────
// GET /api/staff-reporting/visitors?employeeId=&from=&to=&visitorType=
export const getVisitorsApi = (params = {}) =>
  axios.get(`${API_BASE_URL}/api/staff-reporting/visitors`, { params });

// POST /api/staff-reporting/visitors — body { employeeId?, date, rows }
export const addVisitorsApi = (payload) =>
  axios.post(`${API_BASE_URL}/api/staff-reporting/visitors`, payload);

export const updateVisitorApi = (id, payload) =>
  axios.put(`${API_BASE_URL}/api/staff-reporting/visitors/${id}`, payload);

export const deleteVisitorApi = (id) =>
  axios.delete(`${API_BASE_URL}/api/staff-reporting/visitors/${id}`);

// Streams the server-built workbook (All / CP / Customers sheets) to a file.
export const downloadVisitorsWorkbook = async (params = {}) => {
  const res = await axios.get(
    `${API_BASE_URL}/api/staff-reporting/visitors/export`,
    { params, responseType: "blob" },
  );
  const disposition = res.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : "visitors.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
